import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { distance, type Point } from '../../doc/geometry';
import type { Geometry } from '../../doc/schema';
import { useBoard, usePane, usePaneStore, useReadOnly } from '../context';
import { CUTOUT_MESSAGES, cutOut, cutoutPolygon, imageAt, imageUnderPolygon, itemLocalToWorld, type CutoutResult } from '../cutout';
import { flash } from '../flash';
import { toolSettings } from '../toolSettings';
import { screenToWorld, worldToScreen, type Camera } from './camera';
import { isRejectedTouch } from './DrawOverlay';

const TAP_PX = 4;

/**
 * Screen-space canvas for the cutout tool: outlines the image under the pointer, draws the
 * lasso / rectangle / ellipse being dragged out, and on release cuts that piece out of the
 * image (see cutout.ts). Pointers it does not take fall through to the viewport and pan.
 */
export function CutoutOverlay() {
  const active = usePane((s) => s.tool === 'cutout');
  const readOnly = useReadOnly();
  if (!active || readOnly) return null;
  return <CutoutCanvas />;
}

interface Live {
  pointerId: number;
  /** Screen position of the press (tap detection). */
  start: Point;
  startWorld: Point;
  /** Current pointer, world. */
  end: Point;
  /** Flat world coords of the lasso. */
  ring: number[];
  sourceId: string | null;
  dragged: boolean;
}

/** An outline being rendered into a piece; stays on screen until the cut is done. */
interface Busy {
  poly: number[];
  sourceId: string;
}

function CutoutCanvas() {
  const board = useBoard();
  const pane = usePaneStore();
  const size = usePane((s) => s.size);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const live = useRef<Live | null>(null);
  const busy = useRef<Busy | null>(null);
  const hover = useRef<string | null>(null);
  const liveTouch = useRef(false);
  const dpr = window.devicePixelRatio || 1;

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = Math.round(size.w * dpr);
    c.height = Math.round(size.h * dpr);
    render();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, dpr]);

  // Safari runs its touch defaults for pointers we ignore; cancel them at the touch level.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const swallow = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();
    };
    const opts: AddEventListenerOptions = { passive: false };
    c.addEventListener('touchstart', swallow, opts);
    c.addEventListener('touchmove', swallow, opts);
    c.addEventListener('touchend', swallow, opts);
    return () => {
      c.removeEventListener('touchstart', swallow);
      c.removeEventListener('touchmove', swallow);
      c.removeEventListener('touchend', swallow);
    };
  }, []);

  // the outlines are in screen space: redraw when the camera moves (wheel zoom while idle)
  useEffect(
    () =>
      pane.subscribe((s, prev) => {
        if (s.camera !== prev.camera) render();
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pane],
  );

  const toLocal = (e: { clientX: number; clientY: number }): Point => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const outline = (ctx: CanvasRenderingContext2D, g: Geometry, cam: Camera) => {
    const corners = [
      { x: 0, y: 0 },
      { x: g.w, y: 0 },
      { x: g.w, y: g.h },
      { x: 0, y: g.h },
    ];
    ctx.beginPath();
    corners.forEach((c, i) => {
      const p = worldToScreen(cam, itemLocalToWorld(g, c));
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });
    ctx.closePath();
  };

  const render = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, c.width / dpr, c.height / dpr);
    const cam = pane.getState().camera;
    const accent = getComputedStyle(c).getPropertyValue('--accent').trim() || '#2b5bd7';
    const l = live.current;
    const b = busy.current;
    const sourceId = b ? b.sourceId : l ? l.sourceId : hover.current;
    const source = sourceId ? board.index.get(sourceId) : undefined;
    if (source) {
      outline(ctx, source, cam);
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
    const poly = b ? b.poly : l && l.dragged ? cutoutPolygon(toolSettings.getState().cutoutMode, l.startWorld, l.end, l.ring) : null;
    if (!poly || poly.length < 4) return;
    ctx.beginPath();
    for (let i = 0; i < poly.length; i += 2) {
      const p = worldToScreen(cam, { x: poly[i]!, y: poly[i + 1]! });
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.fillStyle = accent;
    ctx.globalAlpha = b ? 0.25 : 0.12;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    ctx.stroke();
    ctx.setLineDash([]);
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const l = live.current;
    if (l) {
      // a second finger beside the first is a gesture (pinch, two-finger tap), not an outline
      if (e.pointerType === 'touch' && liveTouch.current) {
        live.current = null;
        if (e.currentTarget.hasPointerCapture(l.pointerId)) e.currentTarget.releasePointerCapture(l.pointerId);
        render();
      }
      return;
    }
    if (busy.current) return;
    const s = pane.getState();
    if (e.button !== 0 && e.pointerType !== 'touch') return; // middle/right: fall through (pan)
    if (s.spaceHeld) return;
    if (isRejectedTouch(e.pointerType)) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    liveTouch.current = e.pointerType === 'touch';
    const local = toLocal(e);
    const world = screenToWorld(s.camera, local);
    const source = imageAt(board, world, s.selection);
    live.current = {
      pointerId: e.pointerId,
      start: local,
      startWorld: world,
      end: world,
      ring: [world.x, world.y],
      sourceId: source?.id ?? null,
      dragged: false,
    };
    render();
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const l = live.current;
    const s = pane.getState();
    if (!l) {
      if (busy.current || e.pointerType === 'touch') return;
      const over = imageAt(board, screenToWorld(s.camera, toLocal(e)), s.selection);
      const id = over?.id ?? null;
      if (id !== hover.current) {
        hover.current = id;
        render();
      }
      return;
    }
    if (l.pointerId !== e.pointerId) return;
    const native = e.nativeEvent;
    const events = typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
    const list = events.length ? events : [native];
    const lasso = toolSettings.getState().cutoutMode === 'lasso';
    for (const ev of list) {
      const local = toLocal(ev);
      const world = screenToWorld(s.camera, local);
      if (!l.dragged && distance(local, l.start) >= TAP_PX) l.dragged = true;
      l.end = world;
      if (!lasso) continue;
      const n = l.ring.length;
      const lastScreen = worldToScreen(s.camera, { x: l.ring[n - 2]!, y: l.ring[n - 1]! });
      if (distance(lastScreen, local) < 2) continue;
      l.ring.push(world.x, world.y);
    }
    render();
  };

  const finish = async (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const l = live.current;
    if (!l || l.pointerId !== e.pointerId) return;
    live.current = null;
    const el = e.currentTarget;
    if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
    if (!l.dragged) {
      render();
      return;
    }
    const ts = toolSettings.getState();
    const poly = cutoutPolygon(ts.cutoutMode, l.startWorld, l.end, l.ring);
    const source = (l.sourceId ? board.index.get(l.sourceId) : undefined) ?? imageUnderPolygon(board, poly);
    if (!source) {
      render();
      flash(el, CUTOUT_MESSAGES['no-source']);
      return;
    }
    busy.current = { poly, sourceId: source.id };
    render();
    let result: CutoutResult;
    try {
      result = await cutOut({ board, pane }, source.id, poly, { feather: ts.cutoutFeather, punch: ts.cutoutPunch });
    } catch (err) {
      console.warn('Cutout failed:', err);
      result = 'unavailable';
    }
    busy.current = null;
    if (result === 'ok') {
      // the piece is selected: hand over to the select tool so it can be dragged away at once
      flash(el, ts.cutoutPunch ? 'Cut out' : 'Cut out (the original is untouched)');
      pane.getState().setTool('select');
      return;
    }
    render();
    flash(el, CUTOUT_MESSAGES[result]);
  };

  return (
    <canvas
      ref={canvasRef}
      className="draw-overlay cutout-overlay"
      style={{ width: size.w, height: size.h }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(e) => void finish(e)}
      onPointerCancel={(e) => void finish(e)}
    />
  );
}
